// AuthModal.jsx - Version améliorée avec gestion d'erreurs séparée
import React, { useState, useEffect } from 'react';
import { 
  X, Sparkles, Crown, CheckCircle, AlertTriangle, Loader2, Target, 
  Eye, EyeOff, Check, AlertCircle 
} from 'lucide-react';
import { auth } from '../firebase';  
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  sendPasswordResetEmail, 
  signInWithPopup, 
  GoogleAuthProvider 
} from 'firebase/auth';  
import GoogleIcon from '@mui/icons-material/Google'; 
import { useI18n } from '../CP-Multilingue/useI18n';

const AuthModal = ({ mode, onClose, onSuccess, onModeChange, demoObjective }) => {
  const { t, isRTL, currentLanguage } = useI18n();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [errorState, setErrorState] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);

  // États pour la validation du mot de passe
  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    uppercase: false,
    number: false,
    isValid: false
  });

  // Validation du mot de passe en temps réel
  useEffect(() => {
    const validation = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      number: /[0-9]/.test(password)
    };
    validation.isValid = validation.length && validation.uppercase && validation.number;
    
    setPasswordValidation(validation);
  }, [password]);

  // Réinitialiser le formulaire quand le mode change
  useEffect(() => {
    setEmail('');
    setPassword('');
    setFirstName('');
    setLastName('');
    setErrorState('');
    setShowSuccess(false);
    setShowPassword(false);
    setPasswordFocused(false);
  }, [mode]);

  // Empêcher le scroll du body quand le modal est ouvert
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Validation des données du formulaire
  const validateFormData = () => {
    const errors = [];
    
    if (!email.trim()) {
      errors.push(t('auth.common.errors.emailRequired', 'L\'adresse email est requise.'));
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.push(t('auth.common.errors.emailInvalid', 'L\'adresse email n\'est pas valide.'));
    }
    
    if (!password) {
      errors.push(t('auth.common.errors.passwordRequired', 'Le mot de passe est requis.'));
    } else if (mode === 'register' && !passwordValidation.isValid) {
      errors.push(t('auth.register.errors.passwordComplexity', 'Le mot de passe ne respecte pas les critères de sécurité.'));
    } else if (mode === 'login' && password.length < 6) {
      errors.push(t('auth.common.errors.passwordTooShort', 'Le mot de passe doit contenir au moins 6 caractères.'));
    }
    
    if (mode === 'register') {
      if (!firstName.trim()) {
        errors.push(t('auth.register.errors.firstNameRequired', 'Le prénom est requis.'));
      }
      if (!lastName.trim()) {
        errors.push(t('auth.register.errors.lastNameRequired', 'Le nom est requis.'));
      }
    }
    
    return errors;
  };

  // Fonction pour gérer la création de compte avec gestion d'erreurs séparée
  const handleRegister = async () => {
    console.log('🔄 Début de création du compte pour:', email);
    
    let user = null;
    let accountCreated = false;
    
    try {
      // Étape 1: Créer le compte Firebase
      console.log('📝 Création du compte Firebase...');
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      user = userCredential.user;
      accountCreated = true;
      
      console.log('✅ Compte Firebase créé avec succès:', user.uid);
      
      // Étape 2: Mettre à jour le profil (non-critique)
      try {
        console.log('👤 Mise à jour du profil utilisateur...');
        
        // Vérifier si updateProfile existe
        if (typeof user.updateProfile === 'function') {
          await user.updateProfile({ 
            displayName: `${firstName.trim()} ${lastName.trim()}` 
          });
          console.log('✅ Profil utilisateur mis à jour avec succès');
        } else {
          console.log('ℹ️ updateProfile non disponible, profil non mis à jour');
          // Alternative : sauvegarder le nom dans votre backend si nécessaire
        }
      } catch (profileError) {
        console.warn('⚠️ Erreur lors de la mise à jour du profil (non-critique):', profileError);
        // Le compte existe, on continue le processus
      }
      
      // Étape 3: Animation de succès et redirection
      console.log('🎉 Processus d\'inscription terminé avec succès');
      setShowSuccess(true);
      setTimeout(() => {
        onSuccess(user);
      }, 1500);
      
    } catch (error) {
      console.error('❌ Erreur lors de la création du compte:', error);
      console.error('Code d\'erreur:', error.code);
      console.error('Message:', error.message);
      
      // Si le compte a été créé mais qu'une erreur ultérieure survient
      if (accountCreated && user) {
        console.log('ℹ️ Le compte a été créé malgré l\'erreur, redirection...');
        setShowSuccess(true);
        setTimeout(() => {
          onSuccess(user);
        }, 1500);
        return;
      }
      
      // Gestion des erreurs de création de compte Firebase
      let errorMessage = '';
      switch (error.code) {
        case 'auth/email-already-in-use':
          errorMessage = t('auth.register.errors.emailExists', 'Cette adresse email est déjà utilisée.');
          break;
        case 'auth/weak-password':
          errorMessage = t('auth.register.errors.weakPassword', 'Le mot de passe doit contenir au moins 6 caractères.');
          break;
        case 'auth/invalid-email':
          errorMessage = t('auth.register.errors.invalidEmail', 'L\'adresse email n\'est pas valide.');
          break;
        case 'auth/operation-not-allowed':
          errorMessage = t('auth.register.errors.operationNotAllowed', 'L\'inscription par email/mot de passe est désactivée.');
          break;
        case 'auth/network-request-failed':
          errorMessage = t('auth.common.errors.networkError', 'Erreur de réseau. Vérifiez votre connexion internet.');
          break;
        default:
          errorMessage = t('auth.register.errors.general', `Erreur d'inscription: ${error.message || error.code || 'Erreur inconnue'}`);
      }
      
      setErrorState(errorMessage);
    }
  };

  // Fonction pour gérer la connexion
  const handleLogin = async () => {
    console.log('🔄 Tentative de connexion pour:', email);
    
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      if (!user) {
        setErrorState(t('auth.login.errors.invalidCredentials', 'Échec de la connexion. Veuillez vérifier vos identifiants.'));
        return;
      }
      
      console.log('✅ Connexion réussie pour:', user.uid);
      onSuccess(user);
      
    } catch (error) {
      console.error('❌ Erreur de connexion:', error);
      
      let errorMessage = '';
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = t('auth.login.errors.userNotFound', 'Aucun compte trouvé avec cette adresse email.');
          break;
        case 'auth/wrong-password':
          errorMessage = t('auth.login.errors.wrongPassword', 'Mot de passe incorrect.');
          break;
        case 'auth/invalid-email':
          errorMessage = t('auth.login.errors.invalidEmail', 'L\'adresse email n\'est pas valide.');
          break;
        case 'auth/user-disabled':
          errorMessage = t('auth.login.errors.userDisabled', 'Ce compte a été désactivé.');
          break;
        case 'auth/too-many-requests':
          errorMessage = t('auth.login.errors.tooManyRequests', 'Trop de tentatives. Veuillez réessayer plus tard.');
          break;
        case 'auth/network-request-failed':
          errorMessage = t('auth.common.errors.networkError', 'Erreur de réseau. Vérifiez votre connexion internet.');
          break;
        default:
          errorMessage = t('auth.login.errors.general', `Erreur de connexion : ${error.message || error.code || 'Erreur inconnue'}`);
      }
      
      setErrorState(errorMessage);
    }
  };

  // Fonction principale d'authentification avec gestion d'erreurs améliorée
  const handleAuth = async () => {
    setLoading(true);
    setErrorState('');
    
    // Validation des données
    const validationErrors = validateFormData();
    if (validationErrors.length > 0) {
      setErrorState(validationErrors[0]);
      setLoading(false);
      return;
    }
    
    try {
      if (mode === 'register') {
        await handleRegister();
      } else {
        await handleLogin();
      }
    } catch (error) {
      console.error('❌ Erreur inattendue dans handleAuth:', error);
      setErrorState(t('auth.common.errors.unexpected', 'Une erreur inattendue est survenue. Veuillez réessayer.'));
    } finally {
      setLoading(false);
    }
  };

  // Gestion de l'authentification Google avec erreurs améliorées
  const handleGoogleAuth = async () => {
    const provider = new GoogleAuthProvider();
    
    try {
      setLoading(true);
      setErrorState('');
      
      console.log('🔄 Tentative de connexion Google...');
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      
      console.log('✅ Connexion Google réussie:', user.uid);
      
      if (mode === 'register') {
        setShowSuccess(true);
        setTimeout(() => {
          onSuccess(user);
        }, 1500);
      } else {
        onSuccess(user);
      }
      
    } catch (error) {
      console.error('❌ Erreur Google Auth:', error);
      
      let errorMessage = '';
      switch (error.code) {
        case 'auth/popup-closed-by-user':
          errorMessage = t('auth.common.errors.popupClosed', 'La fenêtre de connexion a été fermée.');
          break;
        case 'auth/popup-blocked':
          errorMessage = t('auth.common.errors.popupBlocked', 'La fenêtre popup a été bloquée par votre navigateur.');
          break;
        case 'auth/cancelled-popup-request':
          errorMessage = t('auth.common.errors.popupCancelled', 'Demande de connexion annulée.');
          break;
        case 'auth/network-request-failed':
          errorMessage = t('auth.common.errors.networkError', 'Erreur de réseau. Vérifiez votre connexion internet.');
          break;
        default:
          errorMessage = t('auth.common.errors.google', `Erreur de connexion Google : ${error.message || error.code || 'Erreur inconnue'}`);
      }
      
      setErrorState(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Réinitialisation du mot de passe avec validation
  const handlePasswordReset = async () => {
    if (!email.trim()) {
      setErrorState(t('auth.login.errors.emailRequired', 'Veuillez entrer votre adresse email.'));
      return;
    }
    
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrorState(t('auth.common.errors.emailInvalid', 'L\'adresse email n\'est pas valide.'));
      return;
    }
    
    try {
      setLoading(true);
      console.log('🔄 Envoi de l\'email de réinitialisation à:', email);
      
      await sendPasswordResetEmail(auth, email);
      
      console.log('✅ Email de réinitialisation envoyé');
      setErrorState(t('auth.login.resetEmailSent', 'Un email de réinitialisation a été envoyé.'));
      
    } catch (error) {
      console.error('❌ Erreur lors de la réinitialisation:', error);
      
      let errorMessage = '';
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = t('auth.login.errors.userNotFound', 'Aucun compte trouvé avec cette adresse email.');
          break;
        case 'auth/invalid-email':
          errorMessage = t('auth.common.errors.emailInvalid', 'L\'adresse email n\'est pas valide.');
          break;
        case 'auth/network-request-failed':
          errorMessage = t('auth.common.errors.networkError', 'Erreur de réseau. Vérifiez votre connexion internet.');
          break;
        default:
          errorMessage = t('auth.login.errors.resetFailed', `Erreur lors de l'envoi de l'email : ${error.message || 'Erreur inconnue'}`);
      }
      setErrorState(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleAuth();
    }
  };

  // Styles RTL
  const textAlign = isRTL ? 'text-right' : 'text-left';
  const flexDirection = isRTL ? 'flex-row-reverse' : 'flex-row';

  // Composant pour les critères de mot de passe
  const PasswordCriteria = () => (
    <div className={`mt-2 space-y-1 ${textAlign}`}>
      <div className={`flex items-center text-xs ${flexDirection}`}>
        {passwordValidation.length ? (
          <Check className="w-3 h-3 text-green-500 mr-2" />
        ) : (
          <AlertCircle className="w-3 h-3 text-gray-400 mr-2" />
        )}
        <span className={passwordValidation.length ? 'text-green-600' : 'text-gray-500'}>
          {t('auth.password.criteria.length', 'Au moins 8 caractères')}
        </span>
      </div>
      <div className={`flex items-center text-xs ${flexDirection}`}>
        {passwordValidation.uppercase ? (
          <Check className="w-3 h-3 text-green-500 mr-2" />
        ) : (
          <AlertCircle className="w-3 h-3 text-gray-400 mr-2" />
        )}
        <span className={passwordValidation.uppercase ? 'text-green-600' : 'text-gray-500'}>
          {t('auth.password.criteria.uppercase', 'Au moins une majuscule')}
        </span>
      </div>
      <div className={`flex items-center text-xs ${flexDirection}`}>
        {passwordValidation.number ? (
          <Check className="w-3 h-3 text-green-500 mr-2" />
        ) : (
          <AlertCircle className="w-3 h-3 text-gray-400 mr-2" />
        )}
        <span className={passwordValidation.number ? 'text-green-600' : 'text-gray-500'}>
          {t('auth.password.criteria.number', 'Au moins un chiffre')}
        </span>
      </div>
    </div>
  );

  // Animation de succès
  if (showSuccess) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            {t('auth.register.welcome', 'Bienvenue chez Aladinn !')}
          </h3>
          <p className="text-gray-600 mb-4">
            {demoObjective 
              ? t('auth.register.accountCreatedWithObjective', `Votre compte a été créé avec succès. Nous allons maintenant créer votre premier objectif : "${demoObjective}"`)
              : t('auth.register.accountCreated', 'Votre compte a été créé avec succès. Redirection en cours...')
            }
          </p>
          <div className={`flex items-center justify-center ${flexDirection}`}>
            <Loader2 className="w-5 h-5 text-purple-600 animate-spin mr-2" />
            <span className="text-purple-600">{t('auth.register.initializing', 'Initialisation...')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        
        {/* Header du modal */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className={`flex items-center space-x-3 ${flexDirection}`}>
            <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div className={textAlign}>
              <h2 className="text-xl font-bold text-gray-900">
                {mode === 'register' 
                  ? t('auth.register.title', 'Rejoignez Aladinn') 
                  : t('auth.login.title', 'Bon retour !')
                }
              </h2>
              <p className="text-sm text-gray-600">
                {mode === 'register' 
                  ? t('auth.register.subtitle', 'Révélez votre génie avec l\'IA') 
                  : t('auth.login.subtitle', 'Connectez-vous à votre compte')
                }
              </p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2">
          
          {/* Section gauche - Formulaire */}
          <div className="p-6 lg:p-8">
            {errorState && (
              <div className={`mb-4 p-3 rounded-lg border flex items-start ${
                errorState.includes('envoyé') 
                  ? 'bg-green-50 border-green-200 text-green-800' 
                  : 'bg-red-50 border-red-200 text-red-800'
              }`}>
                {errorState.includes('envoyé') ? (
                  <CheckCircle className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertTriangle className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" />
                )}
                <span className="text-sm">{errorState}</span>
              </div>
            )}

            {/* Affichage de l'objectif démo si présent */}
            {demoObjective && mode === 'register' && (
              <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <div className={`flex items-start space-x-3 ${flexDirection}`}>
                  <Target className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                  <div className={textAlign}>
                    <h4 className="font-medium text-purple-800 mb-1">
                      {t('auth.register.yourObjective', 'Votre objectif')}
                    </h4>
                    <p className="text-sm text-purple-700">"{demoObjective}"</p>
                    <p className="text-xs text-purple-600 mt-1">
                      {t('auth.register.objectiveNote', 'Après inscription, nous créerons automatiquement cet objectif pour vous.')}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Champs du formulaire */}
            {mode === 'register' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={`block text-sm font-medium text-gray-700 mb-2 ${textAlign}`}>
                    {t('auth.register.firstName', 'Prénom')} *
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder={t('auth.register.firstNamePlaceholder', 'Votre prénom')}
                    dir={isRTL ? 'rtl' : 'ltr'}
                    required
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium text-gray-700 mb-2 ${textAlign}`}>
                    {t('auth.register.lastName', 'Nom')} *
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder={t('auth.register.lastNamePlaceholder', 'Votre nom')}
                    dir={isRTL ? 'rtl' : 'ltr'}
                    required
                  />
                </div>
              </div>
            )}

            <div className="mb-4">
              <label className={`block text-sm font-medium text-gray-700 mb-2 ${textAlign}`}>
                {t('auth.common.email', 'Adresse email')} *
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                placeholder={t('auth.common.emailPlaceholder', 'votre@email.com')}
                dir={isRTL ? 'rtl' : 'ltr'}
                required
              />
            </div>

            <div className="mb-6">
              <label className={`block text-sm font-medium text-gray-700 mb-2 ${textAlign}`}>
                {t('auth.common.password', 'Mot de passe')} *
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={handleKeyPress}
                  onFocus={() => setPasswordFocused(true)}
                  onBlur={() => setPasswordFocused(false)}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder={t('auth.common.passwordPlaceholder', '••••••••')}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              
              {/* Critères de mot de passe pour l'inscription */}
              {mode === 'register' && (passwordFocused || password.length > 0) && (
                <PasswordCriteria />
              )}
            </div>

            {/* Bouton principal */}
            <button 
              onClick={handleAuth}
              disabled={loading || (mode === 'register' && !passwordValidation.isValid)}
              className={`w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center ${flexDirection}`}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {mode === 'register' 
                    ? t('auth.register.creating', 'Création du compte...')
                    : t('auth.login.connecting', 'Connexion...')
                  }
                </>
              ) : (
                <>
                  {mode === 'register' ? (
                    <>
                      <Crown className="w-4 h-4 mr-2" />
                      {t('auth.register.createAccount', 'Créer mon compte gratuit')}
                    </>
                  ) : (
                    t('auth.login.signIn', 'Se connecter')
                  )}
                </>
              )}
            </button>

            {/* Bouton Google */}
            <button
              onClick={handleGoogleAuth}
              disabled={loading}
              className={`w-full mt-3 bg-white text-gray-700 py-3 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors shadow-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center ${flexDirection}`}
            >
              <GoogleIcon style={{ width: '20px', height: '20px', marginRight: '8px' }} />
              {mode === 'register' 
                ? t('auth.register.signUpGoogle', 'S\'inscrire avec Google')
                : t('auth.login.signInGoogle', 'Se connecter avec Google')
              }
            </button>

            {/* Actions secondaires */}
            <div className="mt-6 text-center space-y-2">
              <button 
                onClick={() => onModeChange(mode === 'register' ? 'login' : 'register')}
                className="text-purple-600 hover:text-purple-700 font-medium text-sm"
              >
                {mode === 'register' 
                  ? t('auth.register.alreadyAccount', 'Vous avez déjà un compte ? Se connecter')
                  : t('auth.login.newToAladinn', 'Nouveau chez Aladinn ? S\'inscrire gratuitement')
                }
              </button>
              
              {mode === 'login' && (
                <button 
                  onClick={handlePasswordReset}
                  disabled={loading}
                  className="block text-gray-600 hover:text-gray-700 text-sm mx-auto disabled:opacity-50"
                >
                  {t('auth.login.forgotPassword', 'Mot de passe oublié ?')}
                </button>
              )}
            </div>

            {/* Mentions légales pour l'inscription */}
            {mode === 'register' && (
              <p className={`mt-4 text-xs text-gray-500 text-center ${textAlign}`}>
                {t('auth.register.legalStart', 'En créant votre compte, vous acceptez nos')}{' '}
                <button className="text-purple-600 hover:underline">
                  {t('auth.register.termsOfService', 'conditions d\'utilisation')}
                </button>
                {' '}{t('auth.register.legalAnd', 'et notre')}{' '}
                <button className="text-purple-600 hover:underline">
                  {t('auth.register.privacyPolicy', 'politique de confidentialité')}
                </button>.
              </p>
            )}
          </div>

          {/* Section droite - Avantages et motivation */}
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 lg:p-8 border-l border-gray-200">
            <div className="h-full flex flex-col justify-center">
              
              {mode === 'register' ? (
                // Contenu pour l'inscription
                <div>
                  <h3 className={`text-xl font-bold text-purple-800 mb-4 ${textAlign}`}>
                    {t('auth.register.whyJoin', 'Pourquoi rejoindre Aladinn ?')}
                  </h3>
                  
                  <div className="space-y-4 mb-6">
                    <div className={`flex items-start space-x-3 ${flexDirection}`}>
                      <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <div className={textAlign}>
                        <h4 className="font-semibold text-purple-800">
                          {t('auth.register.benefits.intelligence.title', 'Service Personnalisé')}
                        </h4>
                        <p className="text-sm text-purple-700">
                          {t('auth.register.benefits.intelligence.description', 'Votre assistant IA analyse vos objectifs et crée des plans sur-mesure')}
                        </p>
                      </div>
                    </div>
                    
                    <div className={`flex items-start space-x-3 ${flexDirection}`}>
                      <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <div className={textAlign}>
                        <h4 className="font-semibold text-purple-800">
                          {t('auth.register.benefits.resources.title', 'Ressources Expertes')}
                        </h4>
                        <p className="text-sm text-purple-700">
                          {t('auth.register.benefits.resources.description', 'Accès à des milliers de sources analysées et validées par IA')}
                        </p>
                      </div>
                    </div>
                    
                    <div className={`flex items-start space-x-3 ${flexDirection}`}>
                      <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <div className={textAlign}>
                        <h4 className="font-semibold text-purple-800">
                          {t('auth.register.benefits.tracking.title', 'Suivi Intelligent')}
                        </h4>
                        <p className="text-sm text-purple-700">
                          {t('auth.register.benefits.tracking.description', 'Progression mesurée et ajustements automatiques de votre parcours')}
                        </p>
                      </div>
                    </div>
                    
                    <div className={`flex items-start space-x-3 ${flexDirection}`}>
                      <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <div className={textAlign}>
                        <h4 className="font-semibold text-purple-800">
                          {t('auth.register.benefits.free.title', '100% Gratuit')}
                        </h4>
                        <p className="text-sm text-purple-700">
                          {t('auth.register.benefits.free.description', 'Aucune carte de crédit requise, commencez immédiatement')}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Témoignage rapide */}
                  <div className="bg-white/60 rounded-lg p-4 border border-purple-200">
                    <p className={`text-sm text-purple-700 italic mb-2 ${textAlign}`}>
                      {t('auth.register.testimonial.quote', '"Aladinn a transformé ma façon d\'apprendre. En 3 mois, j\'ai atteint des objectifs qui me semblaient impossibles."')}
                    </p>
                    <div className={`flex items-center ${flexDirection}`}>
                      <div className="w-6 h-6 bg-purple-300 rounded-full flex items-center justify-center mr-2">
                        <span className="text-xs text-purple-800 font-bold">M</span>
                      </div>
                      <span className="text-xs text-purple-600">
                        {t('auth.register.testimonial.author', 'Marie D., Entrepreneuse')}
                      </span>
                    </div>
                  </div>

                  {/* Stats rapides */}
                  <div className="mt-6 grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <div className="text-lg font-bold text-purple-800">12K+</div>
                      <div className="text-xs text-purple-600">
                        {t('auth.register.stats.objectives', 'Objectifs atteints')}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-purple-800">96%</div>
                      <div className="text-xs text-purple-600">
                        {t('auth.register.stats.satisfaction', 'Satisfaction')}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                // Contenu pour la connexion
                <div>
                  <h3 className={`text-xl font-bold text-purple-800 mb-4 ${textAlign}`}>
                    {t('auth.login.welcomeBack', 'Bon retour sur Aladinn !')}
                  </h3>
                  
                  <p className={`text-purple-700 mb-6 ${textAlign}`}>
                    {t('auth.login.description', 'Retrouvez vos objectifs, votre progression et continuez votre parcours de transformation avec l\'IA.')}
                  </p>

                  {/* Fonctionnalités qui vous attendent */}
                  <div className="space-y-3 mb-6">
                    <div className={`flex items-center text-sm text-purple-700 ${flexDirection}`}>
                      <Target className="w-4 h-4 mr-2 text-purple-500" />
                      {t('auth.login.features.savedProgress', 'Vos objectifs et progression sauvegardés')}
                    </div>
                    <div className={`flex items-center text-sm text-purple-700 ${flexDirection}`}>
                      <Sparkles className="w-4 h-4 mr-2 text-purple-500" />
                      {t('auth.login.features.aiRecommendations', 'Recommandations IA personnalisées')}
                    </div>
                    <div className={`flex items-center text-sm text-purple-700 ${flexDirection}`}>
                      <CheckCircle className="w-4 h-4 mr-2 text-purple-500" />
                      {t('auth.login.features.contextualChat', 'Chat contextuel avec votre assistant')}
                    </div>
                  </div>

                  {/* Message motivant */}
                  <div className="bg-white/60 rounded-lg p-4 border border-purple-200">
                    <p className={`text-sm text-purple-700 text-center ${textAlign}`}>
                      <span className="font-semibold">
                        {t('auth.login.motivation.title', 'Prêt à continuer ?')}
                      </span><br />
                      {t('auth.login.motivation.subtitle', 'Votre assistant IA vous attend pour poursuivre votre parcours de réussite.')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;